import React, { useEffect, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Eraser, CheckCircle2, Loader2 } from "lucide-react";

export default function SignatureCanvas({ caseId, existing, onChange, defaultSignerName = "", hideSignerInput = false, uploadPath = null }) {
  const canvasRef = useRef(null);
  const { accessToken } = useAuth();
  const [drawing, setDrawing] = useState(false);
  const [empty, setEmpty] = useState(true);
  const [signer, setSigner] = useState(existing?.signer_name || defaultSignerName || "");
  const [saving, setSaving] = useState(false);
  // Auto-save bookkeeping
  const [autoSavedAt, setAutoSavedAt] = useState(null);
  const autoSaveTimer = useRef(null);
  const dirtyRef = useRef(false); // unsaved strokes since last auto-save

  // Auto-fill from customer data as soon as it becomes available,
  // but only if the user has not typed anything yet and nothing is already saved.
  useEffect(() => {
    if (!existing?.signer_name && defaultSignerName && !signer) {
      setSigner(defaultSignerName);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [defaultSignerName, existing?.signer_name]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const parent = canvas.parentElement;
    const resize = () => {
      const ratio = window.devicePixelRatio || 1;
      const cssWidth = parent.clientWidth;
      if (!cssWidth) return;
      // Höhe deutlich erhöht (220 → 320) für komfortables Unterschreiben.
      // Auf Tablet/Handy noch etwas höher (380), damit der Geschädigte
      // genug Platz hat um die Unterschrift natürlich auszuführen.
      const cssHeight = cssWidth < 640 ? 380 : 320;
      // Mobile: Das Öffnen/Schließen der Bildschirmtastatur feuert ein
      // resize-Event OHNE Breitenänderung — Canvas dann NICHT neu aufbauen,
      // sonst wird die bereits gezeichnete Unterschrift gelöscht!
      if (canvas.__cssW === cssWidth) return;
      // Vorhandene Striche sichern und nach dem Resize zurückzeichnen.
      let snapshot = null;
      if (canvas.width > 0 && canvas.height > 0 && canvas.__cssW) {
        snapshot = document.createElement("canvas");
        snapshot.width = canvas.width;
        snapshot.height = canvas.height;
        snapshot.getContext("2d").drawImage(canvas, 0, 0);
      }
      canvas.__cssW = cssWidth;
      // Bind both buffer-size AND CSS-size explicitly so getBoundingClientRect
      // reports the same coordinate-space as we draw in.
      canvas.width = Math.round(cssWidth * ratio);
      canvas.height = Math.round(cssHeight * ratio);
      canvas.style.width = cssWidth + "px";
      canvas.style.height = cssHeight + "px";
      const ctx = canvas.getContext("2d");
      // Reset any prior transform — `canvas.width=` already does this, but be
      // explicit so cumulative scale() across multiple resizes can't happen.
      ctx.setTransform(1, 0, 0, 1, 0, 0);
      ctx.scale(ratio, ratio);
      ctx.lineWidth = 2.2;
      ctx.lineCap = "round";
      ctx.lineJoin = "round";
      ctx.strokeStyle = "#09090B";
      if (snapshot) {
        ctx.drawImage(snapshot, 0, 0, snapshot.width, snapshot.height, 0, 0, cssWidth, cssHeight);
      }
    };
    resize();
    window.addEventListener("resize", resize);
    return () => window.removeEventListener("resize", resize);
  }, []);

  // Restore the saved signature image whenever an `existing` record exists.
  // Without this the canvas is empty when the user comes back to the step.
  useEffect(() => {
    const path = existing?.storage_path;
    const canvas = canvasRef.current;
    if (!path || !canvas || !accessToken) return;

    const backend = process.env.REACT_APP_BACKEND_URL || "";
    const url = `${backend}/api/files/download/${encodeURI(path)}?auth=${encodeURIComponent(accessToken)}`;

    let cancelled = false;
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => {
      if (cancelled) return;
      const ctx = canvas.getContext("2d");
      const cssW = canvas.clientWidth;
      const cssH = canvas.clientHeight || 320;
      const scale = Math.min(cssW / img.width, cssH / img.height);
      const dw = img.width * scale;
      const dh = img.height * scale;
      const dx = (cssW - dw) / 2;
      const dy = (cssH - dh) / 2;
      // clear & re-draw fits the saved signature into the (possibly differently sized) canvas
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      ctx.drawImage(img, dx, dy, dw, dh);
      setEmpty(false);
      dirtyRef.current = false;
    };
    img.onerror = () => { /* token expired or 404 — leave canvas empty */ };
    img.src = url;
    return () => { cancelled = true; };
  }, [existing?.storage_path, accessToken]);

  const pos = (e) => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const t = e.touches?.[0];
    const x = (t?.clientX ?? e.clientX) - rect.left;
    const y = (t?.clientY ?? e.clientY) - rect.top;
    return { x, y };
  };

  const start = (e) => {
    e.preventDefault();
    setDrawing(true); setEmpty(false);
    dirtyRef.current = true;
    const ctx = canvasRef.current.getContext("2d");
    const { x, y } = pos(e);
    ctx.beginPath();
    ctx.moveTo(x, y);
  };
  const move = (e) => {
    if (!drawing) return;
    e.preventDefault();
    const ctx = canvasRef.current.getContext("2d");
    const { x, y } = pos(e);
    ctx.lineTo(x, y);
    ctx.stroke();
  };
  const end = () => {
    setDrawing(false);
    if (empty) return;
    // Save almost immediately (300 ms grace so users don't fire dozens of requests during a multi-stroke signature).
    if (autoSaveTimer.current) clearTimeout(autoSaveTimer.current);
    autoSaveTimer.current = setTimeout(() => {
      if (dirtyRef.current) save({ silent: true });
    }, 300);
  };

  const clear = () => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    setEmpty(true);
    dirtyRef.current = false;
    setAutoSavedAt(null);
    if (autoSaveTimer.current) clearTimeout(autoSaveTimer.current);
  };

  const save = async ({ silent = false } = {}) => {
    if (empty) {
      if (!silent) toast.error("Bitte zuerst unterschreiben");
      return;
    }
    // Signer-name is required for the manual save button — but for auto-save we
    // accept an empty string and let the user fill it in later. The backend
    // stores whatever is sent.
    if (!silent && !signer.trim()) {
      toast.error("Bitte Name des Unterzeichners eingeben");
      return;
    }
    setSaving(true);
    try {
      const blob = await new Promise((resolve) => canvasRef.current.toBlob(resolve, "image/png"));
      const fd = new FormData();
      fd.append("file", blob, "signature.png");
      fd.append("signer_name", signer || "");
      const { data } = await api.post(uploadPath || `/files/cases/${caseId}/signature`, fd, {
      });
      onChange(data.signature);
      dirtyRef.current = false;
      setAutoSavedAt(new Date());
      if (!silent) toast.success("Unterschrift gespeichert");
    } catch (e) {
      if (!silent) toast.error(apiError(e, "Speichern fehlgeschlagen"));
      // For silent save, still surface the issue in the indicator
      if (silent) setAutoSavedAt(null);
    } finally {
      setSaving(false);
    }
  };

  // Cleanup pending timer on unmount
  useEffect(() => () => { if (autoSaveTimer.current) clearTimeout(autoSaveTimer.current); }, []);

  return (
    <div className="space-y-4" data-testid="signature-widget">
      {!hideSignerInput && (
        <div>
          <Label htmlFor="signer">Unterzeichner</Label>
          <Input
            id="signer"
            value={signer}
            onChange={(e) => setSigner(e.target.value)}
            placeholder="Vor- und Nachname"
            className="mt-1"
            data-testid="signature-signer-input"
          />
        </div>
      )}

      <div className="rounded-xl border border-zinc-200 overflow-hidden bg-white">
        <canvas
          ref={canvasRef}
          className="signature-pad w-full"
          onMouseDown={start}
          onMouseMove={move}
          onMouseUp={end}
          onMouseLeave={end}
          onTouchStart={start}
          onTouchMove={move}
          onTouchEnd={end}
          data-testid="signature-canvas"
        />
        <div className="flex items-center justify-between gap-3 px-3 py-2 bg-zinc-50 border-t border-zinc-200">
          <Button variant="ghost" size="sm" onClick={clear} data-testid="signature-clear">
            <Eraser className="h-4 w-4 mr-1" /> Zurücksetzen
          </Button>
          <div className="flex items-center gap-2">
            <span className="text-[11px] text-zinc-500 inline-flex items-center gap-1" data-testid="signature-autosave-indicator">
              {saving ? (
                <><Loader2 className="h-3 w-3 animate-spin" /> Speichert…</>
              ) : autoSavedAt ? (
                <><CheckCircle2 className="h-3 w-3 text-emerald-600" /> Automatisch gespeichert · {autoSavedAt.toLocaleTimeString("de-DE", { hour: "2-digit", minute: "2-digit" })}</>
              ) : !empty ? (
                <>Wird gleich gespeichert…</>
              ) : null}
            </span>
            <Button size="sm" onClick={() => save()} disabled={saving} data-testid="signature-save">
              {saving ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <CheckCircle2 className="h-4 w-4 mr-1" />}
              Speichern
            </Button>
          </div>
        </div>
      </div>

      {existing && (
        <div className="text-xs text-zinc-500" data-testid="signature-saved-indicator">
          Gespeichert am {existing.signed_at?.slice(0, 10)}{existing.signer_name ? ` • ${existing.signer_name}` : ""}
        </div>
      )}
    </div>
  );
}
